import {
    DataGrid,
    DataGridHeader,
    DataGridHeaderCell,
    DataGridBody,
    DataGridRow,
    DataGridCell,
    Button,
    mergeClasses,
} from '@fluentui/react-components';
import { useActiveConversationsTableStyles } from './ActiveConversationsTable.styles';
import React, {
    useMemo,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';
import { IBrokerConnectionActiveConversationsData } from '../../../model/brokerConnections/IBrokerConnectionActiveConversationsData';
import { ActiveConversationRow } from '../../../model/brokerConnections/ActiveConversationRow';
import { useAppContext } from '../../../model';
import type { ActiveConversationsPreferences } from '../../../hooks/useActiveConversationsPreferences';
import {
    ChevronDown24Regular,
    ChevronRight24Regular,
} from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useSelector } from '../../../store/hooks';
import {
    toggleSelect,
    selectActiveConversationsSelectedIds,
    selectResolvingIds,
    selectMarkingAsResolvedId,
    claimConversation,
} from '../../../store/features/activeConversations/activeConversationsSlice';
import { Tooltip } from '@fluentui/react-components';
import { app } from '@microsoft/teams-js';
import {
    safeLocaleCompare,
    safeDateCompare,
    convertDataToRow,
} from '../../../utils/activeConversationsUtils';
import {
    getCellClassName,
    getColumns,
    getColumnSizingOptions,
    getHeaderCellClassName,
} from './ActiveConversationsTable.helpers';
import {
    ConversationGridItem,
    GroupedConversationSection,
    getConversationRowFromEntry,
} from './ActiveConversationsTable.types';
interface ActiveConversationsTableProps {
    items: IBrokerConnectionActiveConversationsData[];
    groupByStatus: boolean;
    preferences: ActiveConversationsPreferences;
    setPreferences: React.Dispatch<
        React.SetStateAction<ActiveConversationsPreferences>
    >;
    preferencesLoaded: boolean;
    onConversationNotesClick: (conversationId: string) => void;
}

export const ActiveConversationsTable: React.FC<
    ActiveConversationsTableProps
> = ({
    items,
    groupByStatus,
    preferences,
    setPreferences,
    preferencesLoaded,
    onConversationNotesClick,
}) => {
        const { t } = useTranslation();
        const styles = useActiveConversationsTableStyles();
        const dispatch = useAppDispatch();
        const selected = useSelector(selectActiveConversationsSelectedIds);
        const resolvingIds = useSelector(selectResolvingIds);
        const markingAsResolvedId = useSelector(selectMarkingAsResolvedId);
        const { teamsContext } = useAppContext();

        // Track time for auto-expiry of "New" flag and elapsed time updates
        const [now, setNow] = useState(Date.now());
        const [expandedGroups, setExpandedGroups] = useState<
            Record<string, boolean>
        >({});
        const hasAppliedPreferences = useRef(false);
        useEffect(() => {
            const id = setInterval(() => setNow(Date.now()), 1000); // refresh every second for timer
            return () => clearInterval(id);
        }, []);

        useEffect(() => {
            if (!preferencesLoaded) {
                hasAppliedPreferences.current = false;
            }
        }, [preferencesLoaded]);

        const messages: ActiveConversationRow[] = useMemo(
            () => items.map((d) => convertDataToRow(d, now)),
            [items, now]
        );

        const sortedMessages = useMemo(
            () =>
                [...messages].sort((a, b) =>
                    safeDateCompare(
                        b.latestMessageTimeRaw,
                        a.latestMessageTimeRaw
                    )
                ),
            [messages]
        );

        const groupedMessages = useMemo<GroupedConversationSection[]>(() => {
            const groups = new Map<string, ActiveConversationRow[]>();

            sortedMessages.forEach((row) => {
                const statusName = row.conversationStatus?.trim() || 'No status';
                const currentGroup = groups.get(statusName) ?? [];
                currentGroup.push(row);
                groups.set(statusName, currentGroup);
            });

            return Array.from(groups.entries())
                .map(([statusName, rows]) => ({
                    statusName,
                    priority:
                        rows[0]?.conversationStatusPriority ??
                        Number.MAX_SAFE_INTEGER,
                    rows: rows
                        .slice()
                        .sort((a, b) =>
                            safeDateCompare(
                                b.latestMessageTimeRaw,
                                a.latestMessageTimeRaw
                            )
                        ),
                }))
                .sort(
                    (a, b) =>
                        a.priority - b.priority ||
                        safeLocaleCompare(a.statusName, b.statusName)
                );
        }, [sortedMessages]);

        useEffect(() => {
            if (!preferencesLoaded || !hasAppliedPreferences.current) return;

            setPreferences((previous) => ({
                ...previous,
                collapsedStatusGroups: expandedGroups,
            }));
        }, [expandedGroups, preferencesLoaded, setPreferences]);

        useEffect(() => {
            if (!preferencesLoaded) return;

            setExpandedGroups(preferences.collapsedStatusGroups);
            hasAppliedPreferences.current = true;
        }, [preferences.collapsedStatusGroups, preferencesLoaded]);

        useEffect(() => {
            setExpandedGroups((previous) => {
                const next = { ...previous };
                groupedMessages.forEach((section) => {
                    const key = section.statusName;
                    if (!(key in next)) {
                        next[key] = true;
                    }
                });
                return next;
            });
        }, [groupedMessages]);

        const toggleGroup = useCallback((groupKey: string) => {
            setExpandedGroups((previous) => ({
                ...previous,
                [groupKey]: !previous[groupKey],
            }));
        }, []);

        const onToggle = useCallback(
            (id: string) => dispatch(toggleSelect(id)),
            [dispatch]
        );

        // Open Teams deep link inside Teams client when embedded, else fallback to browser tab
        const onOpenLink = useCallback((m: ActiveConversationRow) => {
            if (!m.teamsLink) return;
            (async () => {
                try {
                    await app.initialize();
                    const ctx = await app.getContext();
                    if (!ctx) throw new Error('No Teams context');
                    await app.openLink(m.teamsLink);
                } catch {
                    // Fallback: open in new tab when not running inside Teams or on error
                    window.open(m.teamsLink, '_blank', 'noopener');
                }
            })();
        }, []);

        const handleClaimConversationClick = useCallback(
            async (conversationId: string, currentOwnerId: string | null) => {
                const userId = teamsContext?.user?.id;
                if (!userId) {
                    console.error('User ID not available');
                    return;
                }

                if (currentOwnerId === userId) {
                    console.log('Conversation already claimed by current user');
                    return;
                }

                try {
                    await dispatch(claimConversation(conversationId)).unwrap();
                } catch (error) {
                    console.error(
                        'Failed to claim conversation',
                        conversationId,
                        error
                    );
                }
            },
            [teamsContext, dispatch]
        );

        const columns = useMemo(
            () =>
                getColumns(
                    t,
                    onOpenLink,
                    selected,
                    onToggle,
                    styles,
                    resolvingIds,
                    markingAsResolvedId,
                    handleClaimConversationClick,
                    onConversationNotesClick,
                    teamsContext?.user?.id
                ),
            [
                t,
                onOpenLink,
                selected,
                onToggle,
                styles,
                resolvingIds,
                markingAsResolvedId,
                handleClaimConversationClick,
                onConversationNotesClick,
                teamsContext?.user?.id,
            ]
        );

        const columnSizingOptions = useMemo(
            () => getColumnSizingOptions(groupByStatus),
            [groupByStatus]
        );

        const gridItems = useMemo<ConversationGridItem[]>(() => {
            if (!groupByStatus) {
                return sortedMessages.map((row) => ({
                    kind: 'row' as const,
                    row,
                    groupKey: '',
                }));
            }

            return groupedMessages.flatMap((section) => {
                const groupKey = section.statusName;
                const isExpanded = expandedGroups[groupKey] ?? true;
                const items: ConversationGridItem[] = [
                    {
                        kind: 'group',
                        groupKey,
                        section,
                    },
                ];

                if (isExpanded) {
                    section.rows.forEach((row) => {
                        items.push({
                            kind: 'row',
                            row,
                            groupKey,
                        });
                    });
                }

                return items;
            });
        }, [expandedGroups, groupByStatus, groupedMessages, sortedMessages]);

        const handleRowClick = (e: React.MouseEvent, m: ActiveConversationRow) => {
            const target = e.target as HTMLElement;
            if (
                target.closest(
                    'button, [data-stop-row-click], input[type="checkbox"]'
                )
            ) {
                return;
            }
            onOpenLink(m);
        };

        const dataItems = useMemo<
            Array<ConversationGridItem | ActiveConversationRow>
        >(
            () => (groupByStatus ? gridItems : sortedMessages),
            [groupByStatus, gridItems, sortedMessages]
        );

        const renderConversationGrid = () => (
            <DataGrid
                className={styles.dataGrid}
                items={dataItems}
                columns={columns}
                getRowId={(item) => {
                    const rowItem = item as
                        | ConversationGridItem
                        | ActiveConversationRow
                        | undefined;

                    if (!rowItem) {
                        return '';
                    }

                    if (groupByStatus && 'kind' in rowItem) {
                        return rowItem.kind === 'group'
                            ? rowItem.groupKey
                            : rowItem.row.conversationId;
                    }

                    const conversationRow = getConversationRowFromEntry(rowItem);
                    if (!conversationRow) {
                        return '';
                    }

                    return conversationRow.conversationId;
                }}
                resizableColumns={true}
                sortable={groupByStatus ? false : true}
                sortState={{
                    sortColumn: preferences.sortColumn,
                    sortDirection: preferences.sortDirection,
                }}
                onSortChange={(_, nextSortState) => {
                    if (groupByStatus || !nextSortState.sortColumn) return;

                    const sortColumn = nextSortState.sortColumn.toString();
                    setPreferences((previous) => ({
                        ...previous,
                        sortColumn,
                        sortDirection: nextSortState.sortDirection,
                    }));
                }}
                columnSizingOptions={columnSizingOptions}
            >
                <DataGridHeader className={styles.tableHeader}>
                    <DataGridRow className={styles.tableHeaderRow}>
                        {(column) => (
                            <DataGridHeaderCell
                                key={column.columnId}
                                className={getHeaderCellClassName(
                                    column.columnId.toString(),
                                    styles
                                )}
                            >
                                {column.renderHeaderCell()}
                            </DataGridHeaderCell>
                        )}
                    </DataGridRow>
                </DataGridHeader>
                <DataGridBody className={styles.tableBody}>
                    {(row) => {
                        const entry = (
                            row as {
                                item?: ConversationGridItem | ActiveConversationRow;
                            }
                        ).item as
                            | ConversationGridItem
                            | ActiveConversationRow
                            | undefined;

                        if (!entry) {
                            return null;
                        }

                        if (
                            groupByStatus &&
                            'kind' in entry &&
                            entry.kind === 'group'
                        ) {
                            const section = entry.section;
                            const isExpanded =
                                expandedGroups[entry.groupKey] ?? true;

                            return (
                                <DataGridRow
                                    key={entry.groupKey}
                                    className={styles.groupHeaderRow}
                                >
                                    {(column) => {
                                        const isFirstColumn =
                                            column.columnId ===
                                            columns[0]?.columnId;
                                        const isSecondColumn =
                                            column.columnId ===
                                            columns[1]?.columnId;

                                        if (isFirstColumn) {
                                            return (
                                                <DataGridCell
                                                    key={column.columnId}
                                                    className={
                                                        styles.groupHeaderCell
                                                    }
                                                >
                                                    <Button
                                                        appearance="subtle"
                                                        className={
                                                            styles.groupToggleButton
                                                        }
                                                        onClick={() =>
                                                            toggleGroup(
                                                                entry.groupKey
                                                            )
                                                        }
                                                        aria-expanded={isExpanded}
                                                        type="button"
                                                    >
                                                        <div
                                                            className={
                                                                styles.groupHeader
                                                            }
                                                        >
                                                            <div
                                                                className={
                                                                    styles.statusWrapper
                                                                }
                                                            >
                                                                {isExpanded ? (
                                                                    <ChevronDown24Regular />
                                                                ) : (
                                                                    <ChevronRight24Regular />
                                                                )}
                                                                <div
                                                                    className={
                                                                        styles.conversationStatusIndicator
                                                                    }
                                                                    style={{
                                                                        backgroundColor:
                                                                            section
                                                                                .rows[0]
                                                                                ?.conversationColor
                                                                                ? `#${section.rows[0].conversationColor}`
                                                                                : '#808080',
                                                                    }}
                                                                />
                                                                <span>
                                                                    {
                                                                        section.statusName
                                                                    }
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </Button>
                                                </DataGridCell>
                                            );
                                        }

                                        if (isSecondColumn) {
                                            return (
                                                <DataGridCell
                                                    key={column.columnId}
                                                    className={
                                                        styles.groupCountCell
                                                    }
                                                >
                                                    <span
                                                        className={styles.groupMeta}
                                                    >
                                                        {section.rows.length}{' '}
                                                        {section.rows.length === 1
                                                            ? t(
                                                                'Conversation_Count_Singular'
                                                            )
                                                            : t(
                                                                'Conversation_Count_Plural'
                                                            )}
                                                    </span>
                                                </DataGridCell>
                                            );
                                        }

                                        if (
                                            column.columnId ===
                                            columns[columns.length - 1]?.columnId
                                        ) {
                                            return (
                                                <DataGridCell
                                                    key={column.columnId}
                                                    className={
                                                        styles.groupHeaderSpacerCell
                                                    }
                                                    aria-hidden="true"
                                                />
                                            );
                                        }

                                        return (
                                            <DataGridCell
                                                key={column.columnId}
                                                className={
                                                    styles.groupHeaderSpacerCell
                                                }
                                                aria-hidden="true"
                                            />
                                        );
                                    }}
                                </DataGridRow>
                            );
                        }

                        if (
                            groupByStatus &&
                            'kind' in entry &&
                            entry.kind === 'row'
                        ) {
                            const rowItem = entry.row;

                            return (
                                <Tooltip
                                    content={t('Tooltip_GoToConversation')}
                                    relationship="label"
                                    positioning="below"
                                >
                                    <DataGridRow
                                        key={rowItem.conversationId}
                                        className={mergeClasses(
                                            styles.tableBodyRow,
                                            rowItem.isAutoConnection ||
                                                rowItem.isAutoGeneratedConversation
                                                ? styles.autoGeneratedRow
                                                : ''
                                        )}
                                        onClick={(e: React.MouseEvent) =>
                                            handleRowClick(e, rowItem)
                                        }
                                        tabIndex={0}
                                        role="button"
                                        aria-label="Open Teams conversation"
                                    >
                                        {(column) => (
                                            <DataGridCell
                                                key={column.columnId}
                                                className={getCellClassName(
                                                    column.columnId.toString(),
                                                    styles
                                                )}
                                            >
                                                {column.renderCell(rowItem)}
                                            </DataGridCell>
                                        )}
                                    </DataGridRow>
                                </Tooltip>
                            );
                        }

                        const rowItem = getConversationRowFromEntry(entry);
                        if (!rowItem) {
                            return null;
                        }

                        return (
                            <Tooltip
                                content={t('Tooltip_GoToConversation')}
                                relationship="label"
                                positioning="below"
                            >
                                <DataGridRow
                                    key={rowItem.conversationId}
                                    className={mergeClasses(
                                        styles.tableBodyRow,
                                        rowItem.isAutoConnection ||
                                            rowItem.isAutoGeneratedConversation
                                            ? styles.autoGeneratedRow
                                            : ''
                                    )}
                                    onClick={(e: React.MouseEvent) =>
                                        handleRowClick(e, rowItem)
                                    }
                                    tabIndex={0}
                                    role="button"
                                    aria-label="Open Teams conversation"
                                >
                                    {(column) => (
                                        <DataGridCell
                                            key={column.columnId}
                                            className={getCellClassName(
                                                column.columnId.toString(),
                                                styles
                                            )}
                                        >
                                            {column.renderCell(rowItem)}
                                        </DataGridCell>
                                    )}
                                </DataGridRow>
                            </Tooltip>
                        );
                    }}
                </DataGridBody>
            </DataGrid>
        );

        return (
            <div className={styles.tableContainer}>{renderConversationGrid()}</div>
        );
    };
